/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: HoverPopup.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  11.05.23 4:27 pm.
 * Last modified: 11.05.23 4:27 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { addBuildHash, formatDate } from '../api/helpers';
import { useSectionContainerRef } from '../contexts/SectionContainerRefContext';
import { useSettings } from '../contexts/SettingsContext';
import { useTranslator } from '../contexts/TranslatorContext';
import store from '../eventStore';

const POPUP_HEIGHT = 180; // the height of the popup in pixels. always the same
const POPUP_MARGINS = 10;

const enum POPUP_POSITIONS {
    BOTTOM = 0,
    TOP = 1,
    RIGHT = 2,
    LEFT = 3,
}

const HoverPopup = ({
    jackpotName,
    denomination,
    currency,
    level,
    arrowClass = 'egtd_jpw_hp_arrow_50',
    style = {},
    classes = [],
}: IHoverPopupProps) => {
    const [settings] = useSettings();
    const translate = useTranslator();

    let jackpotLevelValue = store.JACKPOT_STATS_COUNTER.value
        ?.get(jackpotName)
        ?.denominations.get(denomination)
        ?.find(jp => jp.level === level);

    // TODO:: DELETE THIS BEFORE MERGING IN PRODUCTION !!!!!!!!!!!!!!!!!!!
    if (jackpotName === 'InstantLink') {
        jackpotLevelValue = {
            level: 1,
            value: 900,
            formattedValue: '9.00',
            lastWinValue: 1034,
            formattedLastWinValue: '10.34',
            maxWinValue: 5186,
            formattedMaxWinValue: '51.86',
            lastWinDate: new Date('2024-06-10T11:51:02.209Z'),
            maxWinDate: new Date('2024-02-08T07:51:38.680Z'),
            totalWins: 8,
        };
    }
    const popUpRef = useRef<HTMLDivElement | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [popupPossition, setPopupPossition] = useState<POPUP_POSITIONS>(POPUP_POSITIONS.BOTTOM); // if the popup is at the bottom of the jp counter
    const [sectionContainerRef] = useSectionContainerRef();

    const popupPossitionClasses = [
        'egtd_jpw_hp_top_arrow', // component at bottom, arrow on top
        'egtd_jpw_hp_bottom_arrow', // // component at top, arrow on bottom
        'egtd_jpw_hp_left_arrow', // // component at right, arrow on left
        'egtd_jpw_hp_right_arrow', // // component at left, arrow on right
    ];

    const navContainerHeight = document.getElementsByClassName('egt_navigation_container')[0].clientHeight;
    const statusContainerHeight = document.getElementsByClassName('egt_status_container')[0].clientHeight;

    useLayoutEffect(() => {
        const isLandscape = window.innerWidth > window.innerHeight;

        if (popUpRef.current && sectionContainerRef?.current) {
            const {
                bottom: sectionContainerBottom,
                top: sectionContainerTop,
                height: sectionContainerHeight,
            } = sectionContainerRef.current.getBoundingClientRect();

            const parentBoundingRect = popUpRef.current?.parentElement?.getBoundingClientRect() || {
                bottom: sectionContainerHeight - navContainerHeight - statusContainerHeight,
                top: sectionContainerHeight - navContainerHeight - statusContainerHeight,
            };

            const bottomSpace = sectionContainerBottom - parentBoundingRect.bottom;
            const topSpace = parentBoundingRect.top - sectionContainerTop;
            const requiredSpaceBottom = POPUP_HEIGHT + POPUP_MARGINS;
            const requiredSpaceTop = requiredSpaceBottom + 56;

            if (bottomSpace > requiredSpaceBottom) {
                if (popupPossition !== POPUP_POSITIONS.BOTTOM) {
                    setPopupPossition(POPUP_POSITIONS.BOTTOM);
                }
            } else if (topSpace > requiredSpaceTop) {
                if (popupPossition !== POPUP_POSITIONS.TOP) {
                    setPopupPossition(POPUP_POSITIONS.TOP);
                }
            } else {
                if (isLandscape) {
                    if (popupPossition !== POPUP_POSITIONS.RIGHT) {
                        setPopupPossition(POPUP_POSITIONS.RIGHT);
                    }
                } else if (topSpace > bottomSpace) {
                    if (popupPossition !== POPUP_POSITIONS.TOP) {
                        setPopupPossition(POPUP_POSITIONS.TOP);
                    }
                } else if (popupPossition !== POPUP_POSITIONS.BOTTOM) {
                    setPopupPossition(POPUP_POSITIONS.BOTTOM);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionContainerRef]);

    if (!jackpotLevelValue?.totalWins) {
        return <></>;
    }

    return (
        <div
            className={`
                egtd_jpw_hover_popup
                egtd_jpw_hp_arrow
                ${popupPossitionClasses[popupPossition]}
                ${arrowClass}
                ${classes.join(' ')}
            `}
            style={style}
            ref={popUpRef}
        >
            <div className="egtd_jpw_hover_popup_inner ">
                <div className="egtd_jpw_hp_row_container">
                    <div className="egtd_jpw_hp_winning_container">
                        <span>{translate('LARGEST_WIN')}:</span>
                        <span className="egtd_jpw_hp_largest_win_value">
                            {jackpotLevelValue?.formattedMaxWinValue}{' '}
                            <span className="egtd_jpw_hp_win_currency">{currency}</span>
                        </span>
                    </div>
                    <div className="egtd_jpw_hp_date_container">
                        {jackpotLevelValue?.maxWinDate && formatDate(jackpotLevelValue.maxWinDate)}
                    </div>
                </div>
                <div className="egtd_jpw_hp_row_container">
                    <div className="egtd_jpw_hp_winning_container">
                        <span>{translate('LAST_WIN')}:</span>
                        <span className="egtd_jpw_hp_last_win_value">
                            {jackpotLevelValue?.formattedLastWinValue}{' '}
                            <span className="egtd_jpw_hp_win_currency">{currency}</span>
                        </span>
                    </div>
                    <div className="egtd_jpw_hp_date_container">
                        {jackpotLevelValue?.lastWinDate && formatDate(jackpotLevelValue.lastWinDate)}
                    </div>
                </div>
                <div className="egtd_jpw_hp_row_container egtd_jpw_hp_total_wons">
                    <div>{translate('TOTAL_WINS')}:</div>
                    <div className="egtd_jpw_hp_date_container">{jackpotLevelValue?.totalWins}</div>
                </div>
                <div className="egtd_jpw_hp_logo_container">
                    <img alt="" src={addBuildHash(`${settings.host}/assets/portal/ui/EGTD_Logo.svg`)} />
                </div>
            </div>
        </div>
    );
};

interface IHoverPopupProps {
    jackpotName: string;
    denomination: number;
    currency: string;
    level: number;
    style: React.CSSProperties | undefined;
    arrowClass?: string;
    classes?: string[];
}

export default HoverPopup;
